import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Database, Cable, Plus, Trash2, RefreshCw, Loader2, CheckCircle2, XCircle,
  Cloud, KeyRound, Fingerprint, Lock, ShieldCheck, Search, ChevronLeft, ChevronRight,
  Layers, AlertTriangle
} from "lucide-react";

// ---------------------------------------------------------------------------
// LIVE DATA PIPELINE — Azure OneLake
// ---------------------------------------------------------------------------
const AUTH_METHODS = [
  {
    value: "service_principal",
    label: "Service Principal (Client Secret)",
    icon: KeyRound,
    blurb: "App registration in Microsoft Entra ID with client secret. Most common for production.",
    fields: ["tenant_id", "client_id", "client_secret"],
  },
  {
    value: "oauth_user",
    label: "OAuth Delegated (Interactive User)",
    icon: ShieldCheck,
    blurb: "Browser-based delegated sign-in via Microsoft account. Best for development.",
    fields: ["tenant_id"],
  },
  {
    value: "managed_identity",
    label: "Managed Identity",
    icon: Fingerprint,
    blurb: "Azure-managed identity (System or User Assigned). No secrets to store.",
    fields: [],
  },
  {
    value: "account_key",
    label: "Storage Account Key",
    icon: Lock,
    blurb: "Shared key with full access to the storage account. Convenient but high-trust.",
    fields: ["account_key"],
  },
  {
    value: "sas_token",
    label: "SAS Token",
    icon: Cable,
    blurb: "Time-bound, scoped Shared Access Signature. Granular permissions.",
    fields: ["sas_token"],
  },
];

const SYNC_MODES = [
  { value: "scheduled", label: "Scheduled (cron)" },
  { value: "on_demand", label: "On-Demand" },
  { value: "streaming", label: "Streaming (continuous)" },
];

const FIELD_LABELS = {
  tenant_id: "Tenant ID (Directory)",
  client_id: "Client ID (Application)",
  client_secret: "Client Secret",
  account_key: "Storage Account Key",
  sas_token: "SAS Token",
};

const empty = {
  name: "", auth_method: "service_principal",
  workspace: "", lakehouse: "", path: "/Tables",
  tenant_id: "", client_id: "", client_secret: "",
  account_key: "", sas_token: "",
  sync_mode: "scheduled", schedule_cron: "0 */6 * * *",
};

const StatusPill = ({ status }) => {
  const map = {
    connected: { c: "bg-emerald-100 text-emerald-900", icon: CheckCircle2 },
    failed: { c: "bg-red-100 text-red-900", icon: XCircle },
    draft: { c: "bg-slate-100 text-slate-700", icon: AlertTriangle },
    disabled: { c: "bg-zinc-100 text-zinc-600", icon: XCircle },
  };
  const m = map[status] || map.draft;
  const I = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${m.c}`}>
      <I className="w-3 h-3" /> {status}
    </span>
  );
};

const PipelineTab = () => {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(empty);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(null);

  const load = () => api.get("/admin/datasources").then((r) => setSources(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/datasources", form);
      toast.success("Data source saved");
      setForm(empty);
      setShowNew(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const r = await api.post(`/admin/datasources/${id}/test`);
      if (r.data.ok) toast.success("Connection OK (mocked handshake)");
      else toast.error(r.data.message || "Connection failed");
      load();
    } finally { setTesting(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this data source?")) return;
    await api.delete(`/admin/datasources/${id}`);
    toast.success("Deleted");
    load();
  };

  const method = AUTH_METHODS.find((m) => m.value === form.auth_method);

  return (
    <div className="space-y-6">
      {/* Banner */}
      <div className="border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-900">
          <strong className="uppercase tracking-[0.18em]">MOCKED:</strong> The connection test simulates an Azure OneLake handshake. Configurations persist and are validated. Wire <span className="font-mono">azure-storage-file-datalake</span> with real credentials to enable live ingestion.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-emerald-800 flex items-center gap-2"><Cloud className="w-3 h-3" /> Azure OneLake</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">Live Data Pipeline</div>
        </div>
        <button data-testid="new-datasource-btn" onClick={() => setShowNew(!showNew)} className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white hover:bg-emerald-900">
          <Plus className="w-4 h-4" /> {showNew ? "Cancel" : "New Connection"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={submit} data-testid="new-datasource-form" className="border border-emerald-200 bg-emerald-50/40 p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="overline text-slate-500 block mb-1">Connection Name</label>
              <input data-testid="ds-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. BSP-Production-OneLake" className="w-full px-3 py-2 border border-slate-300 text-sm font-mono outline-none focus:border-emerald-700" />
            </div>
            <div>
              <label className="overline text-slate-500 block mb-1">Authentication Method</label>
              <select data-testid="ds-auth-method" value={form.auth_method} onChange={(e) => setForm({ ...form, auth_method: e.target.value })} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white">
                {AUTH_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1.5">{method?.blurb}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="overline text-slate-500 block mb-1">Workspace</label>
              <input data-testid="ds-workspace" required value={form.workspace} onChange={(e) => setForm({ ...form, workspace: e.target.value })} placeholder="bsp-fabric-workspace" className="w-full px-3 py-2 border border-slate-300 text-sm font-mono" />
            </div>
            <div>
              <label className="overline text-slate-500 block mb-1">Lakehouse / Item</label>
              <input data-testid="ds-lakehouse" required value={form.lakehouse} onChange={(e) => setForm({ ...form, lakehouse: e.target.value })} placeholder="currency_pcf_lakehouse" className="w-full px-3 py-2 border border-slate-300 text-sm font-mono" />
            </div>
            <div>
              <label className="overline text-slate-500 block mb-1">Path (optional)</label>
              <input data-testid="ds-path" value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} placeholder="/Tables" className="w-full px-3 py-2 border border-slate-300 text-sm font-mono" />
            </div>
          </div>

          {/* Conditional auth fields */}
          {method?.fields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-emerald-200 pt-5">
              <div className="md:col-span-2 overline text-emerald-800">Credentials — {method.label}</div>
              {method.fields.map((f) => (
                <div key={f}>
                  <label className="overline text-slate-500 block mb-1">{FIELD_LABELS[f] || f}</label>
                  <input
                    data-testid={`ds-${f}`}
                    type={f.includes("secret") || f.includes("key") || f.includes("token") ? "password" : "text"}
                    value={form[f]}
                    onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    placeholder={f === "tenant_id" ? "00000000-0000-0000-0000-000000000000" : "••••••••"}
                    className="w-full px-3 py-2 border border-slate-300 text-sm font-mono"
                  />
                </div>
              ))}
            </div>
          )}

          {method?.value === "managed_identity" && (
            <div className="text-xs text-slate-600 italic border-l-2 border-emerald-700 pl-3">
              Managed Identity uses the platform's assigned Azure identity. No credentials are required to be entered here — the runtime supplies the token.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-emerald-200 pt-5">
            <div>
              <label className="overline text-slate-500 block mb-1">Sync Mode</label>
              <select data-testid="ds-sync-mode" value={form.sync_mode} onChange={(e) => setForm({ ...form, sync_mode: e.target.value })} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white">
                {SYNC_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            {form.sync_mode === "scheduled" && (
              <div>
                <label className="overline text-slate-500 block mb-1">Cron Schedule</label>
                <input data-testid="ds-schedule" value={form.schedule_cron} onChange={(e) => setForm({ ...form, schedule_cron: e.target.value })} className="w-full px-3 py-2 border border-slate-300 text-sm font-mono" />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowNew(false); setForm(empty); }} className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-slate-300 hover:bg-slate-100">Cancel</button>
            <button data-testid="ds-submit" disabled={busy} className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white hover:bg-emerald-900 inline-flex items-center gap-2">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save Connection
            </button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="border border-slate-200 bg-white">
        <table data-testid="datasources-table" className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-3 overline text-slate-500">Name</th>
              <th className="px-4 py-3 overline text-slate-500">Auth</th>
              <th className="px-4 py-3 overline text-slate-500">Workspace</th>
              <th className="px-4 py-3 overline text-slate-500">Lakehouse</th>
              <th className="px-4 py-3 overline text-slate-500">Sync</th>
              <th className="px-4 py-3 overline text-slate-500">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s, i) => (
              <tr key={s.id} data-testid={`ds-row-${s.id}`} className={`${i%2 ? "bg-slate-50/50" : ""} border-b border-slate-100`}>
                <td className="px-4 py-3 font-mono font-semibold text-slate-900">{s.name}</td>
                <td className="px-4 py-3 text-xs">{AUTH_METHODS.find((m)=>m.value===s.auth_method)?.label || s.auth_method}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.workspace}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.lakehouse}</td>
                <td className="px-4 py-3 text-xs">{s.sync_mode}</td>
                <td className="px-4 py-3"><StatusPill status={s.status} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button data-testid={`ds-test-${s.id}`} onClick={() => test(s.id)} disabled={testing===s.id} className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border border-slate-300 hover:bg-slate-900 hover:text-white">
                      {testing===s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Test
                    </button>
                    <button data-testid={`ds-delete-${s.id}`} onClick={() => remove(s.id)} className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-red-700 hover:bg-red-700 hover:text-white border border-red-200">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sources.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No data sources configured. Click "New Connection" to add one.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Last test message */}
      {sources.filter((s)=>s.last_test_message).length > 0 && (
        <div className="border border-slate-200 p-4 bg-slate-50 text-xs space-y-2">
          <div className="overline text-emerald-800">Last test results</div>
          {sources.filter((s)=>s.last_test_message).map((s) => (
            <div key={s.id} className="font-mono text-slate-700">
              <span className="font-semibold text-slate-900">{s.name}</span> · {s.last_test_at?.slice(0,19).replace("T"," ")} → {s.last_test_message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SEMANTIC DATA EXPLORER
// ---------------------------------------------------------------------------
const formatCell = (v) => {
  if (v === null || v === undefined) return <span className="text-slate-300">—</span>;
  if (typeof v === "boolean") return <span className={v ? "text-emerald-700" : "text-slate-500"}>{String(v)}</span>;
  if (typeof v === "number") return <span className="font-mono">{v}</span>;
  if (Array.isArray(v)) return <span className="font-mono text-xs">[{v.map((x) => typeof x==="object" ? JSON.stringify(x) : String(x)).join(", ")}]</span>;
  if (typeof v === "object") return <span className="font-mono text-xs text-slate-600">{JSON.stringify(v)}</span>;
  const s = String(v);
  if (s.length > 80) return <span title={s}>{s.slice(0, 80)}…</span>;
  return s;
};

const ExplorerTab = () => {
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("");
  const limit = 100;

  useEffect(() => { api.get("/admin/explorer/collections").then((r) => {
    setCollections(r.data.collections);
    if (r.data.collections.length && !active) setActive(r.data.collections[0].name);
  }); }, []);

  useEffect(() => {
    if (!active) return;
    setData(null);
    api.get(`/admin/explorer/collections/${active}`, { params: { limit, skip: page * limit } })
      .then((r) => setData(r.data));
  }, [active, page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1;
  const filtered = collections.filter((c) => !filter || c.label.toLowerCase().includes(filter.toLowerCase()) || c.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      {/* Sidebar */}
      <div className="lg:col-span-1 space-y-3">
        <div className="border border-slate-200 p-4 bg-white">
          <div className="overline text-emerald-800 mb-2">Sample Database</div>
          <div className="font-heading text-lg font-bold text-slate-900">{collections.length} collections</div>
          <div className="text-xs text-slate-500 mt-1">{collections.reduce((a,c)=>a+c.count,0).toLocaleString()} total rows</div>
        </div>

        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
          <input
            data-testid="explorer-search"
            value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter collections…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 text-sm bg-white outline-none focus:border-emerald-700"
          />
        </div>

        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
          {filtered.map((c) => (
            <button
              key={c.name}
              data-testid={`collection-${c.name}`}
              onClick={() => { setActive(c.name); setPage(0); }}
              className={`w-full text-left p-3 border transition-colors ${active === c.name ? "border-emerald-700 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-heading font-bold text-sm text-slate-900">{c.label}</div>
                <span className="font-mono text-xs text-slate-500">{c.count}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{c.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Table view */}
      <div className="lg:col-span-3 border border-slate-200 bg-white min-h-[400px]">
        {!data ? (
          <div className="p-12 text-center text-slate-500">Loading collection…</div>
        ) : (
          <>
            <div className="border-b border-slate-200 p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="overline text-emerald-800 flex items-center gap-2"><Database className="w-3 h-3" /> Collection</div>
                  <div className="font-heading text-xl font-bold text-slate-900 mt-1">{data.label} <span className="font-mono text-sm text-slate-500 font-normal">({data.name})</span></div>
                  <div className="text-xs text-slate-600 mt-1 max-w-3xl">{data.description}</div>
                </div>
                <div className="text-right">
                  <div className="overline text-slate-500">Rows</div>
                  <div className="font-heading text-2xl font-bold text-slate-900">{data.total}</div>
                </div>
              </div>
              {data.powers?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {data.powers.map((p) => (
                    <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-emerald-100 text-emerald-900">
                      <Layers className="w-2.5 h-2.5" /> {p}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="overflow-auto max-h-[60vh]">
              <table data-testid="explorer-table" className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr className="text-left">
                    {data.columns.map((c) => (
                      <th key={c} className="px-3 py-2 overline text-slate-500 whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className={`${i%2 ? "bg-slate-50/50" : ""} border-b border-slate-100 hover:bg-emerald-50/40`}>
                      {data.columns.map((c) => (
                        <td key={c} className="px-3 py-2 align-top whitespace-nowrap max-w-xs overflow-hidden text-ellipsis">
                          {formatCell(r[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {data.rows.length === 0 && (
                    <tr><td colSpan={data.columns.length || 1} className="px-3 py-8 text-center text-slate-500">No rows.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between text-xs">
              <div className="text-slate-600 font-mono">
                Showing {data.skip + 1}–{Math.min(data.skip + data.limit, data.total)} of {data.total}
              </div>
              <div className="inline-flex gap-2">
                <button
                  data-testid="page-prev"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-900 hover:text-white"
                >
                  <ChevronLeft className="w-3 h-3" /> Prev
                </button>
                <span className="px-3 py-1.5 font-mono">Page {page + 1} / {totalPages}</span>
                <button
                  data-testid="page-next"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-900 hover:text-white"
                >
                  Next <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MAIN ADMIN PAGE
// ---------------------------------------------------------------------------
export default function Admin() {
  const [tab, setTab] = useState("pipeline");

  return (
    <div data-testid="admin-page" className="space-y-6">
      <div>
        <div className="overline text-emerald-800 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Admin Console</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-1">System Administration</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          Connect external data sources to power the platform with live operational data, and inspect the underlying semantic model that drives every dashboard, KPI and chart.
        </p>
      </div>

      <div className="border-b border-slate-200 flex gap-0">
        <button
          data-testid="tab-pipeline"
          onClick={() => setTab("pipeline")}
          className={`px-5 py-3 text-xs uppercase tracking-[0.2em] font-semibold border-b-2 transition-colors inline-flex items-center gap-2 ${tab === "pipeline" ? "border-emerald-800 text-emerald-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
        >
          <Cable className="w-4 h-4" /> Live Data Pipeline
        </button>
        <button
          data-testid="tab-explorer"
          onClick={() => setTab("explorer")}
          className={`px-5 py-3 text-xs uppercase tracking-[0.2em] font-semibold border-b-2 transition-colors inline-flex items-center gap-2 ${tab === "explorer" ? "border-emerald-800 text-emerald-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
        >
          <Database className="w-4 h-4" /> Semantic Data Explorer
        </button>
      </div>

      {tab === "pipeline" ? <PipelineTab /> : <ExplorerTab />}
    </div>
  );
}
